/**
 * Gestionnaire des listes pour l'éditeur riche
 * Remplace les execCommand obsolètes par une implémentation native
 */
export class ListManager {
    constructor(editor) {
        this.editor = editor;
        this.editorContent = editor.editorContent;
    }
    
    /**
     * Détermine le type de liste parent d'un élément
     */
    getListType(element) {
        if (!element) return null;
        
        let current = element;
        while (current && current !== this.editorContent) {
            if (current.tagName === 'UL') return 'ul';
            if (current.tagName === 'OL') return 'ol';
            current = current.parentNode;
        }
        return null;
    }
    
    /**
     * Trouve l'élément de liste (LI) parent
     */
    getListItem(element) {
        if (!element) return null;
        
        let current = element;
        while (current && current !== this.editorContent) {
            if (current.tagName === 'LI') return current;
            current = current.parentNode;
        }
        return null;
    }
    
    /**
     * Trouve la liste (UL/OL) parent
     */
    getList(element) {
        if (!element) return null;
        
        let current = element;
        while (current && current !== this.editorContent) {
            if (current.tagName === 'UL' || current.tagName === 'OL') return current;
            current = current.parentNode;
        }
        return null;
    }
    
    /**
     * Crée une nouvelle liste du type spécifié
     */
    createList(type, content = '') {
        const list = document.createElement(type);
        const listItem = document.createElement('li');
        
        if (content) {
            listItem.innerHTML = content;
        } else {
            listItem.appendChild(document.createElement('br'));
        }
        
        list.appendChild(listItem);
        return { list, listItem };
    }
    
    /**
     * Convertit un bloc en élément de liste
     */
    convertBlockToListItem(block, listType) {
        if (!block || !block.parentNode) return null;
        
        const { list, listItem } = this.createList(listType, block.innerHTML);
        block.replaceWith(list);
        
        return { list, listItem };
    }
    
    /**
     * Ajoute un élément à une liste existante
     */
    addToExistingList(existingList, content = '') {
        const listItem = document.createElement('li');
        
        if (content) {
            listItem.innerHTML = content;
        } else {
            listItem.appendChild(document.createElement('br'));
        }
        
        existingList.appendChild(listItem);
        return listItem;
    }
    
    /**
     * Insère une liste ordonnée
     */
    insertOrderedList() {
        this.insertList('ol');
    }
    
    /**
     * Insère une liste non ordonnée
     */
    insertUnorderedList() {
        this.insertList('ul');
    }
    
    /**
     * Insère une liste du type spécifié
     */
    insertList(listType) {
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        
        const range = selection.getRangeAt(0);
        let currentElement = range.commonAncestorContainer;
        
        if (currentElement.nodeType === Node.TEXT_NODE) {
            currentElement = currentElement.parentNode;
        }
        
        // Vérifier si on est déjà dans une liste
        const existingListItem = this.getListItem(currentElement);
        const existingList = this.getList(currentElement);
        
        if (existingListItem && existingList) {
            // Si on est dans une liste du même type, on sort de la liste
            if (existingList.tagName.toLowerCase() === listType) {
                this.exitList(existingListItem);
                return;
            }
            // Si on est dans une liste d'un autre type, on la convertit
            else {
                this.convertListType(existingList, listType);
                return;
            }
        }
        
        // Trouver le bloc parent
        let blockElement = currentElement;
        while (blockElement && blockElement.parentNode !== this.editorContent) {
            blockElement = blockElement.parentNode;
        }
        
        if (blockElement && blockElement.parentNode === this.editorContent) {
            // Convertir le bloc en liste
            const result = this.convertBlockToListItem(blockElement, listType);
            if (result) {
                // Placer le curseur dans le nouvel élément de liste
                selection.removeAllRanges();
                const newRange = document.createRange();
                newRange.selectNodeContents(result.listItem);
                newRange.collapse(false);
                selection.addRange(newRange);
            }
        }
    }
    
    /**
     * Convertit une liste d'un type à un autre
     */
    convertListType(list, newType) {
        if (!list || list.tagName.toLowerCase() === newType) return;
        
        const newList = document.createElement(newType);
        
        // Copier tous les éléments de liste
        Array.from(list.children).forEach(li => {
            newList.appendChild(li.cloneNode(true));
        });
        
        list.replaceWith(newList);
        return newList;
    }
    
    /**
     * Sort un élément de liste de sa liste
     */
    exitList(listItem) {
        const list = listItem.parentNode;
        if (!list || (list.tagName !== 'UL' && list.tagName !== 'OL')) return;
        
        // Créer un paragraphe avec le contenu de l'élément de liste
        const paragraph = document.createElement('p');
        paragraph.innerHTML = listItem.innerHTML;
        
        // Si c'est le seul élément de la liste, remplacer toute la liste
        if (list.children.length === 1) {
            list.replaceWith(paragraph);
        } else {
            // Insérer le paragraphe après la liste et supprimer l'élément
            list.parentNode.insertBefore(paragraph, list.nextSibling);
            listItem.remove();
        }
        
        // Placer le curseur dans le nouveau paragraphe
        const selection = window.getSelection();
        selection.removeAllRanges();
        const range = document.createRange();
        range.selectNodeContents(paragraph);
        range.collapse(false);
        selection.addRange(range);
    }
    
    /**
     * Augmente l'indentation d'un élément de liste
     */
    indent() {
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        
        const range = selection.getRangeAt(0);
        let currentElement = range.commonAncestorContainer;
        
        if (currentElement.nodeType === Node.TEXT_NODE) {
            currentElement = currentElement.parentNode;
        }
        
        const listItem = this.getListItem(currentElement);
        if (!listItem) return;
        
        const parentList = listItem.parentNode;
        const previousSibling = listItem.previousElementSibling;
        
        if (!previousSibling || previousSibling.tagName !== 'LI') return;
        
        // Créer ou trouver une sous-liste dans l'élément précédent
        let subList = previousSibling.querySelector('ul, ol');
        if (!subList) {
            subList = document.createElement(parentList.tagName.toLowerCase());
            previousSibling.appendChild(subList);
        }
        
        // Déplacer l'élément dans la sous-liste
        subList.appendChild(listItem);
        
        // Restaurer la sélection
        selection.removeAllRanges();
        const newRange = document.createRange();
        newRange.selectNodeContents(listItem);
        newRange.collapse(false);
        selection.addRange(newRange);
    }
    
    /**
     * Diminue l'indentation d'un élément de liste
     */
    outdent() {
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        
        const range = selection.getRangeAt(0);
        let currentElement = range.commonAncestorContainer;
        
        if (currentElement.nodeType === Node.TEXT_NODE) {
            currentElement = currentElement.parentNode;
        }
        
        const listItem = this.getListItem(currentElement);
        if (!listItem) return;
        
        const currentList = listItem.parentNode;
        const parentListItem = currentList.parentNode;
        
        // Vérifier si on est dans une sous-liste
        if (!parentListItem || parentListItem.tagName !== 'LI') {
            // Si on est au niveau racine, sortir de la liste
            this.exitList(listItem);
            return;
        }
        
        const grandParentList = parentListItem.parentNode;
        
        // Déplacer l'élément au niveau supérieur
        const nextSibling = parentListItem.nextSibling;
        if (nextSibling) {
            grandParentList.insertBefore(listItem, nextSibling);
        } else {
            grandParentList.appendChild(listItem);
        }
        
        // Nettoyer la sous-liste vide si nécessaire
        if (currentList.children.length === 0) {
            currentList.remove();
        }
        
        // Restaurer la sélection
        selection.removeAllRanges();
        const newRange = document.createRange();
        newRange.selectNodeContents(listItem);
        newRange.collapse(false);
        selection.addRange(newRange);
    }
    
    /**
     * Gère la pression de la touche Entrée dans une liste
     */
    handleEnterKey(event) {
        const selection = window.getSelection();
        if (!selection.rangeCount) return false;
        
        const range = selection.getRangeAt(0);
        let currentElement = range.commonAncestorContainer;
        
        if (currentElement.nodeType === Node.TEXT_NODE) {
            currentElement = currentElement.parentNode;
        }
        
        const listItem = this.getListItem(currentElement);
        if (!listItem) return false;
        
        event.preventDefault();
        
        // Si l'élément de liste est vide, sortir de la liste
        if (listItem.textContent.trim() === '') {
            this.exitList(listItem);
            return true;
        }
        
        // Créer un nouvel élément de liste
        const newListItem = document.createElement('li');
        newListItem.appendChild(document.createElement('br'));
        
        // L'insérer après l'élément actuel
        if (listItem.nextSibling) {
            listItem.parentNode.insertBefore(newListItem, listItem.nextSibling);
        } else {
            listItem.parentNode.appendChild(newListItem);
        }
        
        // Placer le curseur dans le nouvel élément
        selection.removeAllRanges();
        const newRange = document.createRange();
        newRange.selectNodeContents(newListItem);
        newRange.collapse(true);
        selection.addRange(newRange);
        
        return true;
    }
}